// src/utils/fixReferralIds.js - One-time script to fix missing referral IDs
import { 
  collection, 
  query, 
  getDocs, 
  updateDoc, 
  doc,
  where 
} from "firebase/firestore";
import { db } from "../firebase/config";

// Generate unique referral ID
const generateUniqueReferralId = async () => {
  let isUnique = false;
  let referralId = "";

  while (!isUnique) {
    // Generate a random 8-character alphanumeric ID
    referralId = Math.random().toString(36).substring(2, 10).toUpperCase();

    // Check if it's unique
    const q = query(
      collection(db, "users"),
      where("myReferralId", "==", referralId)
    );
    const querySnapshot = await getDocs(q);
    isUnique = querySnapshot.empty;
  }

  return referralId;
};

// Main function to fix missing referral IDs
export const fixMissingReferralIds = async () => {
  try {
    console.log("🔧 Starting referral ID fix for all users...");
    
    // Get all users
    const usersQuery = query(collection(db, "users"));
    const usersSnapshot = await getDocs(usersQuery);
    
    const usersWithoutReferralId = [];
    const usersWithReferralId = [];
    
    // Categorize users
    usersSnapshot.docs.forEach((userDoc) => {
      const userData = userDoc.data();
      if (!userData.myReferralId) {
        usersWithoutReferralId.push({
          id: userDoc.id,
          email: userData.email,
          name: userData.name,
          uid: userData.uid
        });
      } else {
        usersWithReferralId.push({
          id: userDoc.id,
          email: userData.email,
          referralId: userData.myReferralId
        });
      }
    });
    
    console.log(`📊 Users without referral ID: ${usersWithoutReferralId.length}`);
    console.log(`📊 Users with referral ID: ${usersWithReferralId.length}`);
    
    // Fix users without referral IDs
    const fixedUsers = [];
    for (const user of usersWithoutReferralId) {
      try {
        console.log(`🔧 Fixing ${user.email}...`);
        
        // Generate unique referral ID
        const newReferralId = await generateUniqueReferralId();
        
        // Update user document
        const userDocRef = doc(db, "users", user.id);
        await updateDoc(userDocRef, {
          myReferralId: newReferralId,
          updatedAt: new Date().toISOString(),
          referralIdFixedAt: new Date().toISOString()
        });
        
        fixedUsers.push({
          email: user.email,
          newReferralId: newReferralId
        });
        
        console.log(`✅ Fixed ${user.email} → ${newReferralId}`);
        
        // Add a small delay to avoid overwhelming the database
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (error) {
        console.error(`❌ Failed to fix ${user.email}:`, error);
      }
    }
    
    console.log(`🎉 COMPLETED! Fixed ${fixedUsers.length} users`);
    console.log("📋 Fixed users:", fixedUsers);
    
    return {
      success: true,
      totalUsers: usersSnapshot.docs.length,
      usersFixed: fixedUsers.length,
      fixedUsers: fixedUsers
    };
    
  } catch (error) {
    console.error("❌ Error fixing referral IDs:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Function to fix a specific user's referral ID
export const fixSpecificUserReferralId = async (email, desiredReferralId = null) => {
  try {
    console.log(`🔧 Fixing referral ID for specific user: ${email}`);
    
    // Find user by email
    const userQuery = query(
      collection(db, "users"),
      where("email", "==", email.toLowerCase())
    );
    const userSnapshot = await getDocs(userQuery);
    
    if (userSnapshot.empty) {
      console.log(`❌ User not found: ${email}`);
      return { success: false, error: 'User not found' };
    }
    
    const userDoc = userSnapshot.docs[0];
    const userData = userDoc.data();
    
    console.log(`📋 Current user data:`, {
      email: userData.email,
      name: userData.name,
      currentReferralId: userData.myReferralId || 'NOT SET'
    });
    
    // Determine referral ID to use
    let referralIdToSet = desiredReferralId;
    if (!referralIdToSet) {
      referralIdToSet = await generateUniqueReferralId();
      console.log(`🎲 Generated new referral ID: ${referralIdToSet}`);
    } else {
      // Check if desired ID is available
      const checkQuery = query(
        collection(db, "users"),
        where("myReferralId", "==", referralIdToSet.toUpperCase())
      );
      const checkSnapshot = await getDocs(checkQuery);
      
      if (!checkSnapshot.empty) {
        console.log(`❌ Desired referral ID ${referralIdToSet} is already taken!`);
        return { success: false, error: 'Referral ID already exists' };
      }
    }
    
    // Update user document
    const userDocRef = doc(db, "users", userDoc.id);
    await updateDoc(userDocRef, {
      myReferralId: referralIdToSet.toUpperCase(),
      updatedAt: new Date().toISOString(),
      referralIdFixedAt: new Date().toISOString()
    });
    
    console.log(`✅ SUCCESS: Updated ${email} with referral ID ${referralIdToSet.toUpperCase()}`);
    
    return {
      success: true,
      email: email,
      oldReferralId: userData.myReferralId || 'NOT SET',
      newReferralId: referralIdToSet.toUpperCase()
    };
    
  } catch (error) {
    console.error(`❌ Error fixing referral ID for ${email}:`, error);
    return { success: false, error: error.message };
  }
};

// Make functions available globally for console access
if (typeof window !== 'undefined') {
  window.fixMissingReferralIds = fixMissingReferralIds;
  window.fixSpecificUserReferralId = fixSpecificUserReferralId;
}